import React, { useMemo, useState } from 'react';
import {
    Alert,
    AutoComplete,
    Button,
    Card,
    Col,
    DatePicker,
    Descriptions,
    Form,
    Grid,
    Input,
    Modal,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
    message,
} from 'antd';
import {
    CheckOutlined,
    CloseOutlined,
    EyeOutlined,
    ReloadOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type { CustomerDTO, RegistrationRecordDTO, SelfRegistrationReportDTO } from '@/shared/api/model';
import { useGetRegistrationReports } from '@/shared/api/client/self-registration';
import { useSearchCustomers } from '@/shared/api/client/customers';
import { useAuthUser } from '@/modules/auth/context/AuthContext';
import { useSelfRegistration } from '../hooks/useSelfRegistration';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

type FiltersState = {
    startDate?: string;
    endDate?: string;
    status?: string;
    step?: string;
    zone?: string;
    name?: string;
    nidaId?: string;
    email?: string;
};

const defaultFilters = (): FiltersState => ({
    startDate: dayjs().subtract(90, 'days').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
});

const statusColorMap: Record<string, string> = {
    COMPLETED: 'green',
    PENDING_OFFICER_REVIEW: 'orange',
    OFFICER_APPROVED: 'blue',
    REJECTED: 'red',
    EXPIRED: 'default',
    ACTIVE: 'blue',
};

const stepOptions = [
    'PHONE_VERIFICATION',
    'TERMS_ACCEPTANCE',
    'EMAIL_VERIFICATION',
    'ELIGIBILITY_CHECK',
    'PERSONAL_INFO',
    'LOCATION_SELECTION',
    'DOCUMENT_UPLOAD',
    'OFFICER_REVIEW',
    'DEPOSIT',
    'DEDUCTION_FORM_REVIEW',
    'REGISTRATION_COMPLETE',
];

const statusOptions = [
    'PENDING_OFFICER_REVIEW',
    'OFFICER_APPROVED',
    'COMPLETED',
    'REJECTED',
    'EXPIRED',
    'ACTIVE',
];

const extractErrorMessage = (error: any) => {
    return error?.response?.data?.message || error?.message || 'Operation failed';
};

const OfficerRequestManagementView: React.FC = () => {
    const navigate = useNavigate();
    const authUser = useAuthUser();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const [form] = Form.useForm();
    const [filters, setFilters] = useState<FiltersState>(defaultFilters);
    const [targetRecord, setTargetRecord] = useState<RegistrationRecordDTO | null>(null);
    const [completeModalOpen, setCompleteModalOpen] = useState(false);
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [customerSearch, setCustomerSearch] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerDTO | null>(null);
    const [manualMatches, setManualMatches] = useState<Record<string, CustomerDTO>>({});

    const { data, isLoading, error, refetch } = useGetRegistrationReports(filters, {
        query: {
            staleTime: 30 * 1000,
        },
    });

    const { completeManagementRegistration, rejectSession } = useSelfRegistration();
    const searchBranch = authUser?.branch || '';
    const userId = authUser?.userId || '';

    const { data: searchResults, isFetching: isSearchingCustomers } = useSearchCustomers(
        searchBranch,
        { query: customerSearch },
        {
            query: {
                enabled: completeModalOpen && !!searchBranch && customerSearch.trim().length >= 2,
                staleTime: 30 * 1000,
            },
        }
    );

    const reportData: SelfRegistrationReportDTO | undefined = (data as any)?.data?.data;
    const records = reportData?.records || [];
    const customerOptions = (searchResults || []).map((customer) => ({
        value: customer.custNo || '',
        label: `${customer.name || 'Unknown'} (${customer.custNo || 'N/A'})`,
    }));

    const availableZones = useMemo(() => {
        return [...new Set(records.map((record) => record.zone).filter(Boolean))].sort();
    }, [records]);

    const canCompleteRecord = (record: RegistrationRecordDTO) => {
        return record.status !== 'COMPLETED'
            && record.status !== 'REJECTED'
            && record.termsAccepted === true;
    };

    const canRejectRecord = (record: RegistrationRecordDTO) => {
        return record.status !== 'COMPLETED' && record.status !== 'REJECTED';
    };

    const resetCompletionState = () => {
        setCustomerSearch('');
        setSelectedCustomer(null);
        setCompleteModalOpen(false);
        setTargetRecord(null);
    };

    const resetRejectionState = () => {
        setRejectionReason('');
        setRejectModalOpen(false);
        setTargetRecord(null);
    };

    const handleFilterSubmit = (values: {
        dateRange?: [Dayjs, Dayjs];
        status?: string;
        step?: string;
        zone?: string;
        name?: string;
        nidaId?: string;
        email?: string;
    }) => {
        setFilters({
            startDate: values.dateRange?.[0]?.format('YYYY-MM-DD') || defaultFilters().startDate,
            endDate: values.dateRange?.[1]?.format('YYYY-MM-DD') || defaultFilters().endDate,
            status: values.status || undefined,
            step: values.step || undefined,
            zone: values.zone || undefined,
            name: values.name || undefined,
            nidaId: values.nidaId || undefined,
            email: values.email || undefined,
        });
    };

    const handleResetFilters = () => {
        const nextDefaults = defaultFilters();
        setFilters(nextDefaults);
        form.resetFields();
        form.setFieldsValue({
            dateRange: [dayjs(nextDefaults.startDate), dayjs(nextDefaults.endDate)],
        });
    };

    const openCompleteModal = (record: RegistrationRecordDTO) => {
        setTargetRecord(record);
        setCustomerSearch('');
        setSelectedCustomer(null);
        setCompleteModalOpen(true);
    };

    const openRejectModal = (record: RegistrationRecordDTO) => {
        setTargetRecord(record);
        setRejectionReason(record.rejectionReason || '');
        setRejectModalOpen(true);
    };

    const handleComplete = async () => {
        if (!targetRecord?.sessionToken) {
            message.error('Missing session token');
            return;
        }

        if (!canCompleteRecord(targetRecord)) {
            message.error('Only requests with accepted terms and conditions can be marked as completed.');
            return;
        }

        try {
            setActionLoading(true);
            await completeManagementRegistration(targetRecord.sessionToken);

            if (selectedCustomer && targetRecord.sessionToken) {
                setManualMatches((current) => ({
                    ...current,
                    [targetRecord.sessionToken!]: selectedCustomer,
                }));
            }

            message.success(
                selectedCustomer?.custNo
                    ? `Request marked completed. Matched customer ${selectedCustomer.name || ''} (${selectedCustomer.custNo}).`
                    : 'Request marked completed successfully.'
            );
            resetCompletionState();
            await refetch();
        } catch (err: any) {
            message.error(extractErrorMessage(err));
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async () => {
        if (!targetRecord?.sessionToken) {
            message.error('Missing session token');
            return;
        }

        if (!canRejectRecord(targetRecord)) {
            message.error('Completed or rejected requests cannot be rejected again.');
            return;
        }

        if (!rejectionReason.trim()) {
            message.error('Please provide a rejection reason');
            return;
        }

        if (!userId) {
            message.error('Unable to identify current user');
            return;
        }

        try {
            setActionLoading(true);
            await rejectSession({
                sessionToken: targetRecord.sessionToken,
                officerId: userId,
                rejectionReason: rejectionReason.trim(),
            });
            message.success('Request rejected successfully.');
            resetRejectionState();
            await refetch();
        } catch (err: any) {
            message.error(extractErrorMessage(err));
        } finally {
            setActionLoading(false);
        }
    };

    const columns = [
        {
            title: 'Applicant',
            key: 'applicant',
            render: (_: unknown, record: RegistrationRecordDTO) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{record.fullName || '-'}</Text>
                    <Text type="secondary">{record.phoneNumber || '-'}</Text>
                    <Text type="secondary">{record.email || '-'}</Text>
                </Space>
            ),
        },
        {
            title: 'Status',
            key: 'status',
            render: (_: unknown, record: RegistrationRecordDTO) => (
                <Space direction="vertical" size={4}>
                    <Tag color={statusColorMap[record.status || ''] || 'default'}>{record.status || '-'}</Tag>
                    <Tag>{(record.currentStep || '-').replace(/_/g, ' ')}</Tag>
                </Space>
            ),
        },
        {
            title: 'Customer Match',
            key: 'customerMatch',
            render: (_: unknown, record: RegistrationRecordDTO) => {
                const manualMatch = record.sessionToken ? manualMatches[record.sessionToken] : undefined;

                return (
                    <Space direction="vertical" size={4}>
                        {record.custNo || record.customerId ? (
                            <>
                                <Tag color="green">Mapped in session</Tag>
                                <Text>Cust No: {record.custNo || '-'}</Text>
                                <Text>Customer ID: {record.customerId || '-'}</Text>
                            </>
                        ) : manualMatch ? (
                            <>
                                <Tag color="blue">Manual match</Tag>
                                <Text>{manualMatch.name || '-'}</Text>
                                <Text>{manualMatch.custNo || '-'}</Text>
                            </>
                        ) : (
                            <Text type="secondary">No customer linked</Text>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Timeline',
            key: 'timeline',
            render: (_: unknown, record: RegistrationRecordDTO) => (
                <Space direction="vertical" size={0}>
                    <Text>Created: {record.createdAt ? dayjs(record.createdAt).format('MMM DD, YYYY HH:mm') : '-'}</Text>
                    <Text>Updated: {record.updatedAt ? dayjs(record.updatedAt).format('MMM DD, YYYY HH:mm') : '-'}</Text>
                    <Text>Completed: {record.completedAt ? dayjs(record.completedAt).format('MMM DD, YYYY HH:mm') : '-'}</Text>
                </Space>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: isMobile ? undefined : 'right' as const,
            render: (_: unknown, record: RegistrationRecordDTO) => {
                const canComplete = canCompleteRecord(record);
                const canReject = canRejectRecord(record);

                return (
                    <Space direction={isMobile ? 'vertical' : 'horizontal'}>
                        <Button
                            icon={<EyeOutlined />}
                            onClick={() => record.sessionToken && navigate(`/registration/review/${record.sessionToken}`)}
                            disabled={!record.sessionToken}
                            size="small"
                        >
                            View
                        </Button>
                        <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            onClick={() => openCompleteModal(record)}
                            disabled={!record.sessionToken || !canComplete}
                            size="small"
                        >
                            Complete
                        </Button>
                        <Button
                            danger
                            icon={<CloseOutlined />}
                            onClick={() => openRejectModal(record)}
                            disabled={!record.sessionToken || !canReject}
                            size="small"
                        >
                            Reject
                        </Button>
                    </Space>
                );
            },
        },
    ];

    return (
        <div style={{ padding: isMobile ? '16px' : '24px' }}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Card>
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Title level={3} style={{ margin: 0 }}>Registration Request Management</Title>
                        <Text type="secondary">
                            Manage all self-registration requests from one place. You can review broader request history, reject requests, or mark them completed after cross-checking an existing customer.
                        </Text>
                    </Space>
                </Card>

                <Card>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleFilterSubmit}
                        initialValues={{
                            dateRange: [dayjs(filters.startDate), dayjs(filters.endDate)],
                        }}
                    >
                        <Row gutter={[16, 8]}>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name="dateRange" label="Date Range">
                                    <RangePicker style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={4}>
                                <Form.Item name="status" label="Status">
                                    <Select allowClear placeholder="All statuses" options={statusOptions.map((value) => ({ value, label: value.replace(/_/g, ' ') }))} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={4}>
                                <Form.Item name="step" label="Step">
                                    <Select allowClear placeholder="All steps" options={stepOptions.map((value) => ({ value, label: value.replace(/_/g, ' ') }))} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={4}>
                                <Form.Item name="zone" label="Zone">
                                    <Select allowClear placeholder="All zones" options={availableZones.map((value) => ({ value, label: value }))} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={8}>
                                <Form.Item name="name" label="Applicant Name">
                                    <Input allowClear placeholder="Applicant name" prefix={<SearchOutlined />} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={6}>
                                <Form.Item name="nidaId" label="NIDA / National ID">
                                    <Input allowClear placeholder="National ID" prefix={<SearchOutlined />} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} lg={6}>
                                <Form.Item name="email" label="Email">
                                    <Input allowClear placeholder="Email address" prefix={<SearchOutlined />} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} lg={10}>
                                <Form.Item label=" ">
                                    <Space wrap>
                                        <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                                            Search
                                        </Button>
                                        <Button icon={<ReloadOutlined />} onClick={handleResetFilters}>
                                            Reset
                                        </Button>
                                        <Button onClick={() => refetch()}>
                                            Refresh
                                        </Button>
                                    </Space>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Card>

                {error && (
                    <Alert
                        type="error"
                        showIcon
                        message="Failed to load registration requests"
                        description={extractErrorMessage(error)}
                    />
                )}

                <Card>
                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                        <Descriptions size="small" column={isMobile ? 1 : 4}>
                            <Descriptions.Item label="Total Records">{reportData?.totalRecords || 0}</Descriptions.Item>
                            <Descriptions.Item label="Completed">{reportData?.completedCount || 0}</Descriptions.Item>
                            <Descriptions.Item label="Pending">{reportData?.pendingCount || 0}</Descriptions.Item>
                            <Descriptions.Item label="Rejected">{reportData?.rejectedCount || 0}</Descriptions.Item>
                        </Descriptions>

                        <Table
                            columns={columns}
                            dataSource={records}
                            rowKey="sessionToken"
                            loading={isLoading}
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showTotal: (total) => `Total ${total} requests`,
                            }}
                            size={isMobile ? 'small' : 'middle'}
                        />
                    </Space>
                </Card>
            </Space>

            <Modal
                title="Mark Request as Completed"
                open={completeModalOpen}
                onCancel={resetCompletionState}
                onOk={handleComplete}
                confirmLoading={actionLoading}
                okText="Mark Completed"
                destroyOnHidden
            >
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <Alert
                        type="info"
                        showIcon
                        message="Optional customer cross-check"
                        description="Use the customer search below to confirm this request matches an existing customer that may have been created through another process before marking the request completed."
                    />

                    <Descriptions size="small" column={1} bordered>
                        <Descriptions.Item label="Applicant">{targetRecord?.fullName || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Phone">{targetRecord?.phoneNumber || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Current Status">{targetRecord?.status || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Current Step">{targetRecord?.currentStep || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Terms Accepted">{targetRecord?.termsAccepted ? 'Yes' : 'No'}</Descriptions.Item>
                    </Descriptions>

                    {targetRecord && !canCompleteRecord(targetRecord) && (
                        <Alert
                            type="warning"
                            showIcon
                            message="This request cannot be completed from the current state"
                            description="Only non-terminal requests with accepted terms and conditions can be marked as completed from this page."
                        />
                    )}

                    <div>
                        <Text strong>Existing Customer Search</Text>
                        <AutoComplete
                            style={{ width: '100%', marginTop: 8 }}
                            value={customerSearch}
                            options={customerOptions}
                            onSearch={(value) => {
                                setCustomerSearch(value);
                                setSelectedCustomer(null);
                            }}
                            onSelect={(value) => {
                                const match = (searchResults || []).find((customer) => customer.custNo === value) || null;
                                setSelectedCustomer(match);
                                setCustomerSearch(match ? `${match.name || 'Unknown'} (${match.custNo || 'N/A'})` : value);
                            }}
                            allowClear
                            onClear={() => {
                                setCustomerSearch('');
                                setSelectedCustomer(null);
                            }}
                            placeholder={searchBranch ? 'Type name or customer number' : 'Current user branch not available'}
                            notFoundContent={isSearchingCustomers ? <Spin size="small" /> : customerSearch.trim().length < 2 ? 'Type at least 2 characters' : 'No customer found'}
                            disabled={!searchBranch}
                        />
                        {selectedCustomer && (
                            <Card size="small" style={{ marginTop: 12 }}>
                                <Descriptions size="small" column={1}>
                                    <Descriptions.Item label="Customer Name">{selectedCustomer.name || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="Customer No">{selectedCustomer.custNo || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="Branch">{selectedCustomer.branch || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="Phone">{selectedCustomer.phone || selectedCustomer.mobile || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="Customer ID">{selectedCustomer.customerId || '-'}</Descriptions.Item>
                                </Descriptions>
                            </Card>
                        )}
                    </div>
                </Space>
            </Modal>

            <Modal
                title="Reject Request"
                open={rejectModalOpen}
                onCancel={resetRejectionState}
                onOk={handleReject}
                confirmLoading={actionLoading}
                okButtonProps={{ danger: true }}
                okText="Reject"
                destroyOnHidden
            >
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <Descriptions size="small" column={1} bordered>
                        <Descriptions.Item label="Applicant">{targetRecord?.fullName || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Phone">{targetRecord?.phoneNumber || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Current Status">{targetRecord?.status || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Current Step">{targetRecord?.currentStep || '-'}</Descriptions.Item>
                    </Descriptions>
                    {targetRecord && !canRejectRecord(targetRecord) && (
                        <Alert
                            type="warning"
                            showIcon
                            message="This request cannot be rejected from the current state"
                            description="Completed or already rejected requests cannot be rejected from this page."
                        />
                    )}
                    <Input.TextArea
                        value={rejectionReason}
                        onChange={(event) => setRejectionReason(event.target.value)}
                        rows={4}
                        placeholder="Provide a rejection reason"
                    />
                </Space>
            </Modal>
        </div>
    );
};

export default OfficerRequestManagementView;
